import { DEFAULT_PREFERENCES } from '@mystiq/contracts';
import type { Transaction } from '@tiptap/pm/state';
import type { Editor } from '@tiptap/react';
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { usePreferences } from '../api/hooks.ts';
import { useApi } from '../auth/session.tsx';
import { Copy, ICON, Stop } from '../icons.ts';
import { formatDuration } from '../lib/format.ts';
import { newId, recordingFilename, recordingFormat, recordingTitle } from '../media/recorder.ts';
import { saveRecording } from '../media/save-recording.ts';
import { matchesShortcut, shortcutLabel } from './shortcut.ts';

/** Where the words go: a note editor's range (followed through later edits), or a text field's range. */
type Target =
  | { kind: 'editor'; editor: Editor; from: number; to: number; selected: string; removed: boolean; detach(): void }
  | { kind: 'field'; element: HTMLTextAreaElement | HTMLInputElement; start: number; end: number; value: string };

type DictationState =
  | { status: 'idle' }
  | { status: 'listening'; elapsedMs: number }
  | { status: 'transcribing' }
  | { status: 'inserted' }
  | { status: 'not-inserted'; text: string; reason: string }
  | { status: 'failed'; message: string }
  | { status: 'saved'; itemId: string }
  | { status: 'notice'; message: string };

type Dictation = { toggle(): void; registerEditor(editor: Editor): () => void; shortcut: string };

const DictationContext = createContext<Dictation | null>(null);

export function useDictation(): Dictation | null {
  return useContext(DictationContext);
}

/** Lets dictation reach this note editor while it has the focus. */
export function useDictationTarget(editor: Editor | null) {
  const dictation = useDictation();
  useEffect(() => (editor && dictation ? dictation.registerEditor(editor) : undefined), [editor, dictation]);
}

/** Stops a little before the server's 10-minute limit, so a clip that runs to the end is still accepted. */
const LONGEST_MS = 10 * 60_000 - 10_000;
const TEXT_FIELDS = new Set(['text', 'search', 'url', 'email', '']);

function fieldTarget(element: Element | null): Target | null {
  if (element instanceof HTMLTextAreaElement || (element instanceof HTMLInputElement && TEXT_FIELDS.has(element.type))) {
    if (element.readOnly || element.disabled) return null;
    return { kind: 'field', element, start: element.selectionStart ?? element.value.length, end: element.selectionEnd ?? element.value.length, value: element.value };
  }
  return null;
}

function editorTarget(editor: Editor): Target {
  const { from, to } = editor.state.selection;
  const target: Extract<Target, { kind: 'editor' }> = {
    kind: 'editor',
    editor,
    from,
    to,
    selected: editor.state.doc.textBetween(from, to, '\n'),
    removed: false,
    detach: () => undefined,
  };
  // Typing elsewhere in the note moves the place along with the text around it.
  const follow = ({ transaction }: { transaction: Transaction }) => {
    if (!transaction.docChanged) return;
    const start = transaction.mapping.mapResult(target.from, -1);
    const end = transaction.mapping.mapResult(target.to, 1);
    if (start.deletedAcross && end.deletedAcross) target.removed = true;
    target.from = start.pos;
    target.to = Math.max(start.pos, end.pos);
  };
  editor.on('transaction', follow);
  target.detach = () => editor.off('transaction', follow);
  return target;
}

/** Inserts dictated words at the target, or says why not (the words are then offered to copy). */
function insert(target: Target, words: string): string | null {
  if (target.kind === 'editor') {
    target.detach();
    const { editor } = target;
    if (editor.isDestroyed || !editor.isEditable) return 'The note you were dictating into is closed or read-only now.';
    if (target.removed) return 'The text where you were dictating was removed meanwhile.';
    const { doc } = editor.state;
    if (target.to > doc.content.size) return 'The note changed too much while you were speaking.';
    // A selection is replaced only if it is still exactly the text that was selected.
    const replace = target.from !== target.to && doc.textBetween(target.from, target.to, '\n') === target.selected;
    const at = replace ? { from: target.from, to: target.to } : { from: target.to, to: target.to };
    const before = at.from > 1 ? doc.textBetween(at.from - 1, at.from, '\n') : '';
    const lead = before && !/\s$/.test(before) ? ' ' : '';
    const paragraphs = words.split(/\n{2,}/).map((paragraph) => paragraph.trim()).filter(Boolean);
    const content =
      paragraphs.length > 1
        ? paragraphs.map((text, index) => ({ type: 'paragraph', content: [{ type: 'text', text: index === 0 ? lead + text : text }] }))
        : [{ type: 'text', text: lead + (paragraphs[0] ?? words) }];
    editor.chain().insertContentAt(at, content).run();
    return null;
  }
  const { element } = target;
  if (!element.isConnected || element.readOnly || element.disabled) return 'The field you were dictating into is closed or read-only now.';
  const current = element.value;
  let { start, end } = target;
  if (current !== target.value) {
    // Something else changed the field: add after the unchanged text before the place, else do not guess.
    if (current.slice(0, start) !== target.value.slice(0, start)) return 'The text changed while you were speaking, so the words were not inserted.';
    end = start;
  }
  const lead = start > 0 && !/\s/.test(current[start - 1] ?? '') ? ' ' : '';
  const next = current.slice(0, start) + lead + words + current.slice(end);
  // Through the native setter, so the page's own change handling sees the new text.
  Object.getOwnPropertyDescriptor(Object.getPrototypeOf(element), 'value')?.set?.call(element, next);
  element.dispatchEvent(new Event('input', { bubbles: true }));
  const caret = start + lead.length + words.length;
  element.setSelectionRange(caret, caret);
  return null;
}

/**
 * Dictation on Mystiq pages: the shortcut (or the microphone button) starts
 * listening, pressing it again writes the words where the person was typing.
 * It works only while this page has the focus and only into Mystiq's own
 * fields; it cannot type into other tabs or apps.
 */
export function DictationProvider({ ws, canWrite, children }: { ws: string; canWrite: boolean; children: ReactNode }) {
  const api = useApi();
  const preferences = usePreferences(ws);
  const shortcut = preferences.data?.dictation.shortcut ?? DEFAULT_PREFERENCES.dictation.shortcut;
  const [state, setState] = useState<DictationState>({ status: 'idle' });
  const editors = useRef(new Set<Editor>());
  const session = useRef<{ target: Target; media: MediaRecorder; stream: MediaStream; chunks: Blob[]; startedAt: number; cancelled: boolean } | null>(null);
  /** A start still waiting for the microphone prompt; cancelled if the shortcut is pressed again meanwhile. */
  const starting = useRef<{ cancelled: boolean } | null>(null);
  /** A clip whose words could not be written down yet: kept to try again or save as a voice note. */
  const failed = useRef<{ clip: Blob; target: Target } | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  const finish = useCallback(() => {
    clearInterval(timer.current);
    const current = session.current;
    if (!current) return;
    for (const track of current.stream.getTracks()) track.stop();
    if (current.media.state !== 'inactive') current.media.stop();
  }, []);

  /** Sends a clip and writes its words at the target; on failure the clip is kept with its target. */
  const writeDown = useCallback(
    async (clip: Blob, target: Target) => {
      failed.current = null;
      setState({ status: 'transcribing' });
      try {
        const dictated = await api.dictate(ws, clip);
        const words = dictated.text.trim();
        if (!words) {
          if (target.kind === 'editor') target.detach();
          setState({ status: 'notice', message: 'No words were heard.' });
          return;
        }
        const problem = insert(target, words);
        setState(problem ? { status: 'not-inserted', text: words, reason: problem } : { status: 'inserted' });
      } catch (error) {
        failed.current = { clip, target };
        setState({ status: 'failed', message: (error as Error).message });
      }
    },
    [api, ws],
  );

  const forgetFailed = useCallback(() => {
    const kept = failed.current;
    failed.current = null;
    if (kept?.target.kind === 'editor') kept.target.detach();
  }, []);

  const start = useCallback(async () => {
    if (!canWrite) {
      setState({ status: 'notice', message: 'Viewers cannot dictate: dictation writes into notes and text.' });
      return;
    }
    const active = document.activeElement;
    const editor = [...editors.current].find((candidate) => !candidate.isDestroyed && candidate.isEditable && (candidate.isFocused || candidate.view.dom.contains(active)));
    const target = editor ? editorTarget(editor) : fieldTarget(active);
    if (!target) {
      setState({ status: 'notice', message: `Click where the words should go (a note or a text field), then press ${shortcutLabel(shortcut)}.` });
      return;
    }
    const format = recordingFormat();
    if (!format || !navigator.mediaDevices?.getUserMedia) {
      if (target.kind === 'editor') target.detach();
      setState({ status: 'notice', message: 'This browser cannot record audio here, so dictation is not available.' });
      return;
    }
    forgetFailed();
    const attempt = { cancelled: false };
    starting.current = attempt;
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    } catch (error) {
      starting.current = null;
      if (target.kind === 'editor') target.detach();
      const name = (error as { name?: string }).name;
      setState({
        status: 'notice',
        message: name === 'NotAllowedError' ? 'Microphone access was blocked. Allow the microphone for this site in your browser to dictate.' : 'The microphone could not be started.',
      });
      return;
    }
    starting.current = null;
    // Cancelled while the browser was asking (the shortcut pressed again, or Esc): release the microphone.
    if (attempt.cancelled) {
      for (const track of stream.getTracks()) track.stop();
      if (target.kind === 'editor') target.detach();
      return;
    }
    const media = new MediaRecorder(stream, { mimeType: format });
    const current = { target, media, stream, chunks: [] as Blob[], startedAt: performance.now(), cancelled: false };
    session.current = current;
    media.ondataavailable = (event) => {
      if (event.data.size) current.chunks.push(event.data);
    };
    media.onstop = () => {
      session.current = null;
      if (current.cancelled) {
        if (target.kind === 'editor') target.detach();
        setState({ status: 'idle' });
        return;
      }
      void writeDown(new Blob(current.chunks, { type: format }), target);
    };
    media.start(250);
    setState({ status: 'listening', elapsedMs: 0 });
    timer.current = setInterval(() => {
      const elapsedMs = performance.now() - current.startedAt;
      if (elapsedMs >= LONGEST_MS) finish();
      else setState({ status: 'listening', elapsedMs });
    }, 250);
  }, [canWrite, shortcut, finish, forgetFailed, writeDown]);

  const toggle = useCallback(() => {
    if (starting.current) {
      starting.current.cancelled = true;
      starting.current = null;
      setState({ status: 'idle' });
    } else if (session.current) finish();
    else if (state.status !== 'transcribing') void start();
  }, [finish, start, state.status]);

  const cancel = useCallback(() => {
    if (starting.current) {
      starting.current.cancelled = true;
      starting.current = null;
      setState({ status: 'idle' });
      return;
    }
    if (!session.current) return;
    session.current.cancelled = true;
    finish();
  }, [finish]);

  /** Keeps a clip that could not be written down as a voice note, so the words are not lost. */
  const saveAsVoiceNote = useCallback(async () => {
    const kept = failed.current;
    if (!kept) return;
    setState({ status: 'transcribing' });
    try {
      const now = Date.now();
      const item = await saveRecording(
        api,
        ws,
        {
          blob: kept.clip,
          title: recordingTitle('voice_note', now),
          filename: recordingFilename('voice_note', now, kept.clip.type || 'audio/webm'),
          capture: 'voice_note',
          uploadKey: `dictation-${newId()}`,
        },
        { renewKey: async () => `dictation-${newId()}` },
      );
      forgetFailed();
      setState({ status: 'saved', itemId: item.id });
    } catch (error) {
      setState({ status: 'failed', message: (error as Error).message });
    }
  }, [api, ws, forgetFailed]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (matchesShortcut(event, shortcut)) {
        event.preventDefault();
        toggle();
      } else if (event.key === 'Escape' && (session.current || starting.current)) {
        event.preventDefault();
        cancel();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [shortcut, toggle, cancel]);

  // Brief confirmations go away on their own.
  useEffect(() => {
    if (state.status !== 'inserted' && state.status !== 'notice') return;
    const hide = setTimeout(() => setState({ status: 'idle' }), state.status === 'inserted' ? 2500 : 6000);
    return () => clearTimeout(hide);
  }, [state]);

  useEffect(
    () => () => {
      cancel();
      forgetFailed();
    },
    [cancel, forgetFailed],
  );

  const registerEditor = useCallback((editor: Editor) => {
    editors.current.add(editor);
    return () => void editors.current.delete(editor);
  }, []);

  return (
    <DictationContext.Provider value={{ toggle, registerEditor, shortcut }}>
      {children}
      {state.status !== 'idle' && (
        <div className="dictation" role={state.status === 'not-inserted' || state.status === 'failed' ? 'alert' : 'status'} aria-live="polite">
          {state.status === 'listening' && (
            <>
              <p className="recorder-line">
                <span className="now-dot" aria-hidden />
                <span className="state">Listening</span>
                <span className="recorder-time">{formatDuration(state.elapsedMs)}</span>
                <span className="meta">Press {shortcutLabel(shortcut)} to write it down, Esc to cancel.</span>
              </p>
              <div className="row-actions">
                <button className="button button-inline" onClick={finish}>
                  <Stop size={ICON.inline} stroke={ICON.stroke} />
                  Write it down
                </button>
                <button className="button button-inline" onClick={cancel}>
                  Cancel
                </button>
              </div>
            </>
          )}
          {state.status === 'transcribing' && (
            <p>
              <span className="state">Writing down what you said.</span>
            </p>
          )}
          {state.status === 'inserted' && (
            <p>
              <span className="state">Inserted.</span>
            </p>
          )}
          {state.status === 'notice' && (
            <p>
              <span className="state">Not dictating.</span> {state.message}
            </p>
          )}
          {state.status === 'failed' && (
            <>
              <p>
                <span className="state">Not written down yet.</span> {state.message} What you said is kept here.
              </p>
              <div className="row-actions">
                <button className="button button-inline" onClick={() => failed.current && void writeDown(failed.current.clip, failed.current.target)}>
                  Try again
                </button>
                <button className="button button-inline" onClick={() => void saveAsVoiceNote()}>
                  Save as a voice note
                </button>
                <button
                  className="button button-inline"
                  onClick={() => {
                    forgetFailed();
                    setState({ status: 'idle' });
                  }}
                >
                  Discard
                </button>
              </div>
            </>
          )}
          {state.status === 'saved' && (
            <div className="row-actions">
              <p>
                <span className="state">Saved as a voice note.</span> It is transcribed there.
              </p>
              <Link to={`/w/${ws}/items/${state.itemId}`} onClick={() => setState({ status: 'idle' })}>
                Open it
              </Link>
              <button className="link-button" onClick={() => setState({ status: 'idle' })}>
                Dismiss
              </button>
            </div>
          )}
          {state.status === 'not-inserted' && (
            <>
              <p>
                <span className="state">Not inserted.</span> {state.reason}
              </p>
              <p className="dictation-text">{state.text}</p>
              <div className="row-actions">
                <button className="button button-inline" onClick={() => void navigator.clipboard.writeText(state.text).catch(() => undefined)}>
                  <Copy size={ICON.inline} stroke={ICON.stroke} />
                  Copy the words
                </button>
                <button className="button button-inline" onClick={() => setState({ status: 'idle' })}>
                  Dismiss
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </DictationContext.Provider>
  );
}
